import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  X as XIcon,
  Send,
  ThumbsUp,
  ThumbsDown,
  Trash2,
  MessageSquareDiff,
  Loader2,
} from "lucide-react";
import api, { unwrap, apiError } from "@/lib/api";
import { EP } from "@/lib/endpoints";
import UserAvatar from "@/components/UserAvatar";
import ModerationWarning from "@/components/ModerationWarning";
import { isModerationFlagged, isModerationError } from "@/lib/moderation";
import { useAuthor } from "@/lib/authors";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/**
 * DifferentViewSheet — 1:1 web port of iOS
 * `mobile/src/components/DifferentViewSheet.tsx` (spec 12 §7).
 *
 * Bottom sheet that opens when the user taps the comment bubble on an
 * OPINION post.  Shows every existing "different view" comment with
 * Agree / Disagree voting + delete-own, and a pinned composer at the
 * bottom (50-word soft cap).
 *
 *   Data source — GET /posts/{post_id} → `interactions.different_views`
 *   Xano returns joined columns as 1-element arrays; `unwrapGrouped`
 *   flattens them.  Voting: POST /different-views/{id}/agree|disagree
 *   (one switchable vote, no DELETE counterpart).  Delete own DV:
 *   DELETE /different-views/{id}.  Create: POST /different-view/{postId}
 *   with { content }.
 */

const WORD_CAP = 50;
const wordCount = (s) => (s.trim() ? s.trim().split(/\s+/).length : 0);
const BRAND = "hsl(var(--brand))";
const AGREE_GREEN = "#3DD68C";
const DANGER = "#FF3B30";

// Xano returns grouped/joined columns as 1-element arrays — unwrap them.
function unwrapGrouped(item) {
  if (!item || typeof item !== "object") return item;
  const out = {};
  for (const [k, v] of Object.entries(item)) {
    out[k] = Array.isArray(v) && v.length === 1 ? v[0] : v;
  }
  return out;
}

function relTime(input) {
  if (!input) return "";
  const d = typeof input === "number" ? new Date(input) : new Date(input);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (Number.isNaN(diff)) return "";
  if (diff < 60) return "now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export default function DifferentViewSheet({
  postId,
  open,
  onClose,
  onCountChange,
}) {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState("");
  const [posting, setPosting] = useState(false);
  const [flagged, setFlagged] = useState(false);

  const words = wordCount(content);
  const over = words > WORD_CAP;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(EP.post(postId));
      let p = unwrap(res.data);
      p = p?.post || p;
      const dvs = (p?.interactions?.different_views || []).map(unwrapGrouped);
      // Merge-safe: keep any optimistic entries the server response
      // doesn't carry yet, so a fresh comment never disappears.
      setItems((prev) => {
        const pending = (prev || []).filter(
          (x) =>
            String(x.id).startsWith("tmp-") &&
            !dvs.some(
              (d) => d.content === x.content && d.user_id === x.user_id
            )
        );
        return [...pending, ...dvs];
      });
    } catch {
      setItems((prev) => prev || []);
    } finally {
      setLoading(false);
    }
  }, [postId]);

  useEffect(() => {
    if (open) {
      setItems(null);
      setFlagged(false);
      setContent("");
      load();
    }
  }, [open, load]);

  // Lock body scroll while the sheet is open + Escape closes it.
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  const submit = async () => {
    if (!content.trim() || over || posting) return;
    const text = content.trim();
    setPosting(true);
    setFlagged(false);
    try {
      const res = await api.post(EP.differentView(postId), { content: text });
      const created = unwrap(res.data);
      const rec = created?.different_view || created?.dv || created;
      // Moderation can flag on a 200 payload too — show the red warning
      // and keep the text so the user can edit it (spec 12).
      if (isModerationFlagged(created) || isModerationFlagged(rec)) {
        setFlagged(true);
        return;
      }
      // Show it immediately; the background reload reconciles.
      const optimistic =
        rec && typeof rec === "object" && rec.content
          ? rec
          : {
              id: `tmp-${postId}-${text.length}`,
              user_id: user?.id,
              content: text,
              created_at: Date.now(),
            };
      setItems((prev) => [optimistic, ...(prev || [])]);
      setContent("");
      toast.success("Different view shared");
      onCountChange?.(1);
      load();
    } catch (e) {
      if (isModerationError(e)) setFlagged(true);
      else toast.error(apiError(e));
    } finally {
      setPosting(false);
    }
  };

  const handleDeleted = (dvId) => {
    setItems((prev) => (prev || []).filter((x) => x.id !== dvId));
    onCountChange?.(-1);
  };

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center"
      style={{ background: "rgba(0,0,0,0.45)" }}
      onClick={onClose}
      data-testid="dv-sheet-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[560px] bg-card rounded-t-[26px] flex flex-col overflow-hidden shadow-2xl animate-dv-slide-up"
        style={{ maxHeight: "82vh" }}
        data-testid="dv-sheet"
      >
        {/* Grabber */}
        <div className="pt-2 flex justify-center">
          <div className="w-10 h-1 rounded-sm bg-border" />
        </div>

        {/* Header — "Different Views (N)" + close X */}
        <div className="flex items-center justify-between px-[18px] pt-1.5 pb-3">
          <h3 className="text-foreground text-[17px] font-extrabold">
            Different Views
            {items && items.length ? ` (${items.length})` : ""}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 -mr-1 flex items-center justify-center hover:opacity-80"
            aria-label="Close different views"
            data-testid="dv-sheet-close"
          >
            <XIcon size={22} className="text-muted-foreground" />
          </button>
        </div>

        {/* Body — loading / empty / list */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          {loading && items === null ? (
            <div className="py-10 flex justify-center">
              <Loader2
                className="w-5 h-5 animate-spin"
                style={{ color: BRAND }}
                data-testid="dv-sheet-loading"
              />
            </div>
          ) : !items || items.length === 0 ? (
            <div
              className="py-11 flex flex-col items-center gap-2.5 px-8 text-center"
              data-testid="dv-sheet-empty"
            >
              <MessageSquareDiff
                size={26}
                className="text-muted-foreground"
                strokeWidth={1.8}
              />
              <p className="text-muted-foreground text-[14.5px] leading-5">
                No different views yet.
                <br />
                Be the first to share another perspective.
              </p>
            </div>
          ) : (
            <div
              className="px-4 pb-3 flex flex-col gap-2.5"
              data-testid="dv-sheet-list"
            >
              {items.map((item) => (
                <DvRow
                  key={String(item.id)}
                  item={item}
                  currentUser={user}
                  onDeleted={handleDeleted}
                />
              ))}
            </div>
          )}
        </div>

        {/* Red Bronto moderation warning — flagged different view.
            Sits directly above the composer per iOS spec 12. */}
        {flagged && (
          <div className="px-4 pt-3">
            <ModerationWarning testId="dv-moderation-warning" />
          </div>
        )}

        {/* Composer — avatar + pill input + brand send button */}
        <div
          className="flex items-end gap-2.5 px-3.5 pt-2.5 pb-3 border-t border-border"
          style={{ paddingBottom: "max(12px, env(safe-area-inset-bottom))" }}
        >
          <div className="shrink-0 pb-1">
            <UserAvatar user={user} size="sm" />
          </div>
          <div
            className="flex-1 min-h-[38px] max-h-[106px] rounded-full border bg-secondary px-4 py-2 flex items-center"
            style={{ borderColor: over ? DANGER : "hsl(var(--border))" }}
          >
            <textarea
              value={content}
              onChange={(e) => {
                setContent(e.target.value);
                if (flagged) setFlagged(false);
              }}
              placeholder="Share your different view…"
              rows={1}
              className="w-full bg-transparent text-foreground text-[15px] resize-none focus:outline-none placeholder:text-muted-foreground"
              style={{ maxHeight: 90 }}
              data-testid="dv-sheet-input"
            />
          </div>
          <button
            type="button"
            disabled={!content.trim() || over || posting}
            onClick={submit}
            className="shrink-0 w-[38px] h-[38px] rounded-full flex items-center justify-center transition-opacity disabled:opacity-40 active:opacity-85"
            style={{ background: BRAND }}
            data-testid="dv-sheet-submit"
            aria-label="Post different view"
          >
            {posting ? (
              <Loader2 className="w-4 h-4 animate-spin text-white" />
            ) : (
              <Send size={15} className="text-white" />
            )}
          </button>
        </div>
        {words > 0 && (
          <div
            className={`px-4 pb-1.5 text-right text-[11.5px] ${
              over ? "text-[#FF3B30]" : "text-muted-foreground"
            }`}
          >
            {words}/{WORD_CAP} words
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

// -----------------------------------------------------------
// Single different-view row: author + content + Agree/Disagree
// -----------------------------------------------------------
function DvRow({ item, currentUser, onDeleted }) {
  const isMine = currentUser && item.user_id === currentUser.id;
  // Author hydration — the DV payload only carries `user_id`; look up the
  // full record via useAuthor (cached per session) so we show the real
  // display name + avatar instead of a "User 14" placeholder.
  const seed = isMine
    ? currentUser
    : {
        username: item.username,
        display_name: item.display_name,
        profile_photo_url: item.profile_photo_url,
      };
  const author = useAuthor(item.user_id, seed);

  const [deleting, setDeleting] = useState(false);
  const [voting, setVoting] = useState(false);
  const initialVote =
    item.viewer_has_agreed || item.viewer_agreed || item.i_agreed
      ? "agree"
      : item.viewer_has_disagreed ||
        item.viewer_disagreed ||
        item.i_disagreed
      ? "disagree"
      : null;
  const [vote, setVote] = useState(initialVote);
  const [counts, setCounts] = useState({
    agree: item.agree_count ?? item.agrees_count ?? item.agrees ?? 0,
    disagree:
      item.disagree_count ?? item.disagrees_count ?? item.disagrees ?? 0,
  });

  const castVote = async (kind) => {
    if (voting || vote === kind) return;
    const prevVote = vote;
    const prevCounts = counts;
    setVote(kind);
    setCounts((c) => ({
      agree:
        c.agree + (kind === "agree" ? 1 : 0) - (prevVote === "agree" ? 1 : 0),
      disagree:
        c.disagree +
        (kind === "disagree" ? 1 : 0) -
        (prevVote === "disagree" ? 1 : 0),
    }));
    setVoting(true);
    try {
      await api.post(
        kind === "agree"
          ? EP.agreeDifferentView(item.id)
          : EP.disagreeDifferentView(item.id),
        {}
      );
    } catch (e) {
      setVote(prevVote);
      setCounts(prevCounts);
      toast.error(apiError(e));
    } finally {
      setVoting(false);
    }
  };

  const handleDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      await api.delete(EP.deleteDifferentView(item.id));
      onDeleted(item.id);
      toast.success("Different view removed");
    } catch (e) {
      toast.error(apiError(e));
      setDeleting(false);
    }
  };

  return (
    <div
      className="rounded-[18px] border border-border bg-background p-3.5"
      data-testid={`dv-sheet-item-${item.id ?? "x"}`}
    >
      <div className="flex items-center gap-2 mb-[7px]">
        <UserAvatar user={author} size="sm" />
        <div className="flex-1 min-w-0">
          <div className="text-foreground text-[13.5px] font-bold truncate">
            {author.display_name || author.username}
          </div>
          <div className="text-muted-foreground text-[11.5px] truncate">
            @{author.username}
            {item.created_at ? ` · ${relTime(item.created_at)}` : ""}
          </div>
        </div>
        {isMine && (
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleting}
            className="p-1 disabled:opacity-40"
            aria-label="Delete different view"
            data-testid={`dv-sheet-delete-${item.id}`}
          >
            {deleting ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />
            ) : (
              <Trash2 size={15} className="text-muted-foreground" />
            )}
          </button>
        )}
      </div>

      <p className="text-foreground text-[14.5px] leading-5 whitespace-pre-wrap break-words">
        {item.content}
      </p>

      <div className="flex gap-[18px] mt-2.5">
        <button
          type="button"
          onClick={() => castVote("agree")}
          className="flex items-center gap-1.5 transition-transform active:scale-95"
          data-testid={`dv-sheet-agree-${item.id ?? "x"}`}
          disabled={voting}
        >
          <ThumbsUp
            size={15}
            style={{
              color: vote === "agree" ? AGREE_GREEN : "hsl(var(--muted-foreground))",
              fill: vote === "agree" ? AGREE_GREEN : "transparent",
            }}
            strokeWidth={2}
          />
          <span
            className="text-[12px] font-bold tabular-nums"
            style={{
              color: vote === "agree" ? AGREE_GREEN : "hsl(var(--muted-foreground))",
            }}
          >
            Agree{counts.agree ? ` · ${counts.agree}` : ""}
          </span>
        </button>
        <button
          type="button"
          onClick={() => castVote("disagree")}
          className="flex items-center gap-1.5 transition-transform active:scale-95"
          data-testid={`dv-sheet-disagree-${item.id ?? "x"}`}
          disabled={voting}
        >
          <ThumbsDown
            size={15}
            style={{
              color: vote === "disagree" ? DANGER : "hsl(var(--muted-foreground))",
              fill: vote === "disagree" ? DANGER : "transparent",
            }}
            strokeWidth={2}
          />
          <span
            className="text-[12px] font-bold tabular-nums"
            style={{
              color: vote === "disagree" ? DANGER : "hsl(var(--muted-foreground))",
            }}
          >
            Disagree{counts.disagree ? ` · ${counts.disagree}` : ""}
          </span>
        </button>
      </div>
    </div>
  );
}
